import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, DownloadSimple, Image as ImageIcon, Sparkle, BookBookmark, Megaphone, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSTER_TYPES = [
  {
    id: 'promo_poster',
    label: 'Promo Poster',
    hint: 'Bold, marketable poster art — great for social media and launch buzz.',
    Icon: Megaphone,
  },
  {
    id: 'book_cover',
    label: 'Book Cover',
    hint: 'Elegant, atmospheric cover art — refined and shelf-ready.',
    Icon: BookBookmark,
  },
];

export const PosterModal = ({ open, onClose, sessionId, documentType, projectName, isAdmin = false }) => {
  const [posters, setPosters] = useState([]);
  const [quotaReached, setQuotaReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [posterType, setPosterType] = useState('book_cover');
  const [customPrompt, setCustomPrompt] = useState('');

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/poster/${sessionId}`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setPosters(r.data.posters || []);
        setQuotaReached(!!r.data.quota_reached);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  const generate = async () => {
    if (generating || (quotaReached && !isAdmin)) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/poster/generate/${sessionId}`,
        { poster_type: posterType, custom_prompt: customPrompt },
        { withCredentials: true, timeout: 240000 },
      );
      setPosters(r.data.posters || []);
      setQuotaReached(!!r.data.quota_reached);
      const currentType = POSTER_TYPES.find((t) => t.id === posterType)?.label || 'Poster';
      toast.success(`${currentType} ready`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Poster generation failed — please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const download = (poster, idx) => {
    const safe = (projectName || 'manuscript').replace(/[^a-z0-9-]+/gi, '_').slice(0, 40);
    const suffix = poster.poster_type === 'promo_poster' ? 'promo' : 'cover';
    const a = document.createElement('a');
    a.href = `data:${poster.mime_type};base64,${poster.data_b64}`;
    a.download = `${safe}-${suffix}-${idx + 1}.png`;
    a.click();
  };

  if (!open) return null;

  const canGenerate = isAdmin || !quotaReached;
  const remainingLabel = isAdmin
    ? 'Host access · unlimited generations'
    : `Complimentary · ${Math.max(0, 2 - (posters?.length || 0))} of 2 remaining`;

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="poster-modal">
      <div className="bg-background border border-border max-w-4xl w-full rounded-lg shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-border/60 shrink-0">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1 flex items-center gap-2">
              {isAdmin ? <Crown size={14} weight="duotone" /> : <Sparkle size={14} weight="duotone" />}
              {remainingLabel}
            </p>
            <h3 className="text-2xl font-serif font-normal text-ivory">Generate cover art for your {documentType || 'work'}</h3>
            <p className="text-xs text-ash mt-2 max-w-xl">
              {isAdmin
                ? 'Admin freedom — generate as many promo posters or book covers as you like.'
                : 'Choose a style and (optionally) add creative direction. You get 2 complimentary posters per project.'}
            </p>
          </div>
          <button onClick={onClose} className="text-ash hover:text-ivory p-1" data-testid="poster-close"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* Poster type toggle */}
          <div className="mb-5">
            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Style</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="poster-type-toggle">
              {POSTER_TYPES.map((t) => {
                const active = posterType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setPosterType(t.id)}
                    disabled={generating}
                    className={`text-left border rounded-lg p-4 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                    data-testid={`poster-type-${t.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <t.Icon size={16} weight="duotone" className={active ? 'text-gold' : 'text-ash'} />
                      <span className={`text-sm font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{t.label}</span>
                    </div>
                    <p className="text-xs text-ash leading-relaxed">{t.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom prompt */}
          <div className="mb-5">
            <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2 block" htmlFor="poster-prompt">
              Creative direction (optional)
            </label>
            <textarea
              id="poster-prompt"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              maxLength={600}
              rows={3}
              disabled={generating}
              placeholder="e.g. moody storm at sea, muted teal palette, single silhouetted figure…"
              className="w-full rounded-md bg-stone/40 border border-border text-bone text-sm font-sans p-3 focus:outline-none focus:border-gold/60"
              data-testid="poster-prompt-input"
            />
            <div className="text-[10px] text-ash mt-1 text-right">{customPrompt.length}/600</div>
          </div>

          {/* Actions / results */}
          {loading ? (
            <div className="text-center text-sm text-ash py-8">Loading…</div>
          ) : (
            <>
              {posters.length === 0 && (
                <div className="text-center py-6" data-testid="poster-empty">
                  <ImageIcon size={40} weight="duotone" className="text-gold/60 mx-auto mb-4" />
                  <p className="text-sm text-bone font-sans mb-5 max-w-md mx-auto">
                    {isAdmin
                      ? 'Click below to generate a poster. This typically takes 20–60 seconds.'
                      : 'Click below to generate your complimentary posters. This typically takes 20–60 seconds.'}
                  </p>
                </div>
              )}

              {posters.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  {posters.map((p, i) => (
                    <figure key={i} className="panel-luxe p-3" data-testid={`poster-item-${i}`}>
                      <img
                        src={`data:${p.mime_type};base64,${p.data_b64}`}
                        alt={`Poster ${i + 1}`}
                        className="w-full h-auto rounded-md"
                      />
                      <figcaption className="mt-3 flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.28em] text-ash">
                          {p.poster_type === 'promo_poster' ? 'Promo' : p.poster_type === 'book_cover' ? 'Cover' : 'Poster'} · {i + 1}
                        </span>
                        <button
                          onClick={() => download(p, i)}
                          className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.24em] text-gold hover:text-gold-dark border border-gold/40 hover:border-gold px-2.5 py-1.5 rounded-md"
                          data-testid={`poster-download-${i}`}
                        >
                          <DownloadSimple size={11} weight="duotone" /> Download PNG
                        </button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}

              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={generate}
                  disabled={generating || !canGenerate}
                  className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="poster-generate-btn"
                >
                  <Sparkle size={14} weight="duotone" />
                  {generating
                    ? 'Generating…'
                    : posters.length === 0
                      ? (isAdmin ? 'Generate poster' : 'Generate my 2 posters')
                      : (isAdmin ? 'Generate another' : quotaReached ? 'Quota reached' : 'Generate next poster')}
                </button>
                {!isAdmin && quotaReached && (
                  <p className="text-[11px] text-ash text-center max-w-md">
                    You&apos;ve used all 2 free posters for this project. Save them somewhere safe — they won&apos;t regenerate.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
